/**
 * Records API Client
 *
 * Handles all HTTP requests to the Records backend API.
 * Uses AuthenticatedApiClient which is self-sufficient (fetches auth dynamically).
 *
 * Key Responsibilities:
 * - Make authenticated requests to /api/v1/records endpoints
 * - Transform API responses from snake_case to camelCase
 * - Handle errors with user-friendly messages
 * - Support filtering, pagination, and advanced queries
 *
 * Usage:
 * ```typescript
 * // Production (no token needed - self-sufficient)
 * const api = getRecordsApi();
 * const records = await api.getRecords({ status: 'open' });
 *
 * // Testing (inject mock token)
 * const api = getRecordsApi('mock-token');
 * ```
 */

import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import type {
  QmsRecord,
  QmsRecordList,
  RecordResponse,
  RecordListResponse,
  UpdateRecordDataResponse,
  CompleteStepResponse,
  RecordFilters,
  CreateRecordInput,
  UpdateRecordInput,
  ArchiveRecordInput,
  CompleteStepInput,
  RecordQueryRequest,
  FieldDefinition,
  FieldDefinitionResponse,
  ConditionRule,
  ConditionRuleResponse,
  WorkflowStepDefinition,
  WorkflowStepDefinitionResponse,
  StepInstance,
  StepInstanceResponse,
  ESignatureValue,
  ESignatureValueResponse,
  FileAttachmentValue,
  FileAttachmentValueResponse,
  RecordAttachment,
  RecordAttachmentResponse,
} from '../types';
import {convertFieldConfigResponse} from '../utils/field-config-transforms';

/**
 * API Client for Records
 *
 * Extends AuthenticatedApiClient (self-sufficient auth).
 */
export class RecordsApiClient extends AuthenticatedApiClient {
  /**
   * Base path for Record endpoints
   */
  private get basePath(): string {
    return '/api/v1/records';
  }

  /**
   * Get list of Records with optional filters and pagination
   */
  async getRecords(filters: RecordFilters = {}): Promise<QmsRecordList> {
    const parameters = new URLSearchParams();

    if (filters.status) {
      parameters.append('status', filters.status);
    }

    if (filters.recordTypeId) {
      parameters.append('record_type_id', filters.recordTypeId);
    }

    if (filters.search) {
      parameters.append('search', filters.search);
    }

    if (filters.assignedToMe !== undefined) {
      parameters.append('assigned_to_me', filters.assignedToMe.toString());
    }

    if (filters.limit !== undefined) {
      parameters.append('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      parameters.append('offset', filters.offset.toString());
    }

    const endpoint =
      parameters.toString().length > 0
        ? `${this.basePath}?${parameters.toString()}`
        : this.basePath;

    const response = await this.get<RecordListResponse>(endpoint);
    return this.convertRecordListResponse(response);
  }

  /**
   * Query Records with advanced filtering, sorting, and pagination
   */
  async queryRecords(queryRequest: RecordQueryRequest): Promise<QmsRecordList> {
    const response = await this.post<RecordListResponse>(
      `${this.basePath}/query`,
      queryRequest
    );

    return this.convertRecordListResponse(response);
  }

  /**
   * Get single Record by ID
   */
  async getRecord(id: string): Promise<QmsRecord> {
    const response = await this.get<RecordResponse>(`${this.basePath}/${id}`);

    return this.convertRecordResponse(response);
  }

  /**
   * Create new Record from a published RecordType
   */
  async createRecord(input: CreateRecordInput): Promise<QmsRecord> {
    const requestBody: Record<string, unknown> = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      record_type_id: input.recordTypeId,
      name: input.name,
    };

    // Step assignees — required when workflow steps exist, omitted for attachment-only records
    if (input.stepAssignees.length > 0) {
      requestBody.step_assignees = input.stepAssignees.map((sa) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        step_order: sa.stepOrder,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        assignee_ids: sa.assigneeIds,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        group_assignee_ids: sa.groupIds,
      }));
    }

    if (input.displayIdSuffix) {
      requestBody.display_id_suffix = input.displayIdSuffix;
    }

    if (input.initialData && Object.keys(input.initialData).length > 0) {
      requestBody.initial_data = input.initialData;
    }

    // Pre-creation attachments (staged files)
    if (input.attachmentFileIds && input.attachmentFileIds.length > 0) {
      requestBody.attachment_file_ids = input.attachmentFileIds;
      requestBody.attachment_filenames = input.attachmentFilenames;
      requestBody.attachment_content_types = input.attachmentContentTypes;
      if (input.attachmentCuiFlags) {
        // Convert boolean CUI flags to metadata dicts for the backend
        const metadataMap: Record<string, Record<string, unknown>> = {};
        for (const [fileId, isCui] of Object.entries(
          input.attachmentCuiFlags
        )) {
          if (isCui) {
            metadataMap[fileId] = {cui: true};
          }
        }

        if (Object.keys(metadataMap).length > 0) {
          requestBody.attachment_metadata = metadataMap;
        }
      }
    }

    if (input.sourceEmailAttachmentId) {
      requestBody.source_email_attachment_id = input.sourceEmailAttachmentId;
    }

    const response = await this.post<RecordResponse>(
      this.basePath,
      requestBody
    );

    return this.convertRecordResponse(response);
  }

  /**
   * Update Record data and/or step assignees
   *
   * Partial update - merges with existing data.
   * Backend returns UpdateRecordDataResponse with record wrapped.
   */
  async updateRecord(id: string, input: UpdateRecordInput): Promise<QmsRecord> {
    const requestBody: Record<string, unknown> = {};

    if (input.data) {
      requestBody.data = input.data;
    }

    if (input.stepAssignees) {
      requestBody.step_assignees = input.stepAssignees.map((sa) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        step_order: sa.stepOrder,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        assignee_ids: sa.assigneeIds,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        group_assignee_ids: sa.groupIds,
      }));
    }

    if (input.name !== undefined) {
      requestBody.name = input.name;
    }

    if (input.displayId !== undefined) {
      requestBody.display_id = input.displayId;
    }

    const response = await this.patch<UpdateRecordDataResponse>(
      `${this.basePath}/${id}`,
      requestBody
    );

    // Unwrap the record from the response
    return this.convertRecordResponse(response.record);
  }

  /**
   * Archive a Record (soft-delete, reversible by admin/quality)
   */
  async archiveRecord(
    id: string,
    input: ArchiveRecordInput = {}
  ): Promise<QmsRecord> {
    const response = await this.post<RecordResponse>(
      `${this.basePath}/${id}/archive`,
      {reason: input.reason}
    );

    return this.convertRecordResponse(response);
  }

  /**
   * Manually close an open Record
   */
  async closeRecord(id: string): Promise<QmsRecord> {
    const response = await this.post<RecordResponse>(
      `${this.basePath}/${id}/close`,
      {}
    );

    return this.convertRecordResponse(response);
  }

  /**
   * Restore an archived Record (admin/quality only)
   */
  async restoreRecord(id: string): Promise<QmsRecord> {
    const response = await this.post<RecordResponse>(
      `${this.basePath}/${id}/restore`,
      {}
    );

    return this.convertRecordResponse(response);
  }

  /**
   * Permanently delete a Record (admin/quality only, irreversible)
   */
  async deleteRecord(id: string): Promise<void> {
    await this.delete(`${this.basePath}/${id}`);
  }

  /**
   * Complete a workflow step
   *
   * Backend returns CompleteStepResponse with record wrapped.
   */
  async completeStep(
    recordId: string,
    stepOrder: number,
    input?: CompleteStepInput
  ): Promise<QmsRecord> {
    const response = await this.post<CompleteStepResponse>(
      `${this.basePath}/${recordId}/steps/${stepOrder}/complete`,
      input?.data ? {data: input.data} : {}
    );

    // Unwrap the record from the response
    return this.convertRecordResponse(response.record);
  }

  /**
   * Reopen the most recently completed workflow step.
   *
   * Backend derives "last" — no step_order in the path. Closed records
   * return to OPEN; the reopened step transitions to IN_PROGRESS.
   */
  async reopenLastStep(recordId: string): Promise<QmsRecord> {
    const response = await this.post<CompleteStepResponse>(
      `${this.basePath}/${recordId}/steps/last/reopen`,
      {}
    );

    return this.convertRecordResponse(response.record);
  }

  /**
   * Sign an e-signature field
   *
   * Convenience method that updates the record data with signature info.
   */
  async signField(recordId: string, fieldKey: string): Promise<QmsRecord> {
    const response = await this.post<RecordResponse>(
      `${this.basePath}/${recordId}/sign/${fieldKey}`,
      {}
    );

    return this.convertRecordResponse(response);
  }

  /**
   * Get My Tasks - Records with pending steps for current user
   */
  async getMyTasks(
    filters: {limit?: number; offset?: number} = {}
  ): Promise<QmsRecordList> {
    const parameters = new URLSearchParams();

    if (filters.limit !== undefined) {
      parameters.append('limit', filters.limit.toString());
    }

    if (filters.offset !== undefined) {
      parameters.append('offset', filters.offset.toString());
    }

    const endpoint =
      parameters.toString().length > 0
        ? `${this.basePath}/my-tasks?${parameters.toString()}`
        : `${this.basePath}/my-tasks`;

    const response = await this.get<RecordListResponse>(endpoint);
    return this.convertRecordListResponse(response);
  }

  /**
   * Upload a file to a file_attachment field via 3-phase presigned upload.
   *
   * Phase 1: Request presigned upload URL from backend
   * Phase 2: PUT file directly to presigned URL (S3 or local token endpoint)
   * Phase 3: Confirm upload with backend to create DB record
   */
  async uploadFile(
    recordId: string,
    fieldKey: string,
    file: File,
    metadata?: Record<string, unknown>
  ): Promise<FileAttachmentValue> {
    const contentType = file.type || 'application/octet-stream';

    // Phase 1: Request presigned upload URL
    const urlResult = await this.requestUploadUrl(recordId, fieldKey, {
      filename: file.name,
      contentType,
      fileSize: file.size,
    });

    if (!urlResult.url) {
      throw new Error('Upload URL not available. Check storage configuration.');
    }

    // Phase 2: PUT directly to presigned URL
    const headers = new Headers();
    headers.set('Content-Type', contentType);
    const uploadResponse = await fetch(urlResult.url, {
      method: 'PUT',
      body: file,
      headers,
    });

    if (!uploadResponse.ok) {
      throw new Error(
        `S3 upload failed with status ${String(uploadResponse.status)}`
      );
    }

    // Phase 3: Confirm upload
    return this.confirmUpload(recordId, fieldKey, {
      fileId: urlResult.fileId,
      filename: file.name,
      contentType,
      metadata,
    });
  }

  /**
   * Phase 1: Request a presigned upload URL for direct upload.
   */
  async requestUploadUrl(
    recordId: string,
    fieldKey: string,
    request: {filename: string; contentType: string; fileSize: number}
  ): Promise<{
    url: string | null;
    fileId: string;
    expiresIn: number;
  }> {
    const response = await this.post<{
      url: string | null;
      file_id: string; // eslint-disable-line @typescript-eslint/naming-convention
      expires_in: number; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/${recordId}/fields/${fieldKey}/files/upload-url`, {
      filename: request.filename,
      content_type: request.contentType, // eslint-disable-line @typescript-eslint/naming-convention
      file_size: request.fileSize, // eslint-disable-line @typescript-eslint/naming-convention
    });
    return {
      url: response.url,
      fileId: response.file_id,
      expiresIn: response.expires_in,
    };
  }

  /**
   * Phase 3: Confirm that a presigned upload completed successfully.
   */
  async confirmUpload(
    recordId: string,
    fieldKey: string,
    request: {
      fileId: string;
      filename: string;
      contentType: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<FileAttachmentValue> {
    const body: Record<string, unknown> = {
      file_id: request.fileId, // eslint-disable-line @typescript-eslint/naming-convention
      filename: request.filename,
      content_type: request.contentType, // eslint-disable-line @typescript-eslint/naming-convention
    };
    if (request.metadata) {
      body.metadata = request.metadata;
    }

    const response = await this.post<FileAttachmentValueResponse>(
      `${this.basePath}/${recordId}/fields/${fieldKey}/files/confirm-upload`,
      body
    );

    return this.convertFileAttachmentValue(response);
  }

  /**
   * Get a pre-signed download URL for a record file.
   */
  async getFileUrl(
    recordId: string,
    fileId: string,
    download = false
  ): Promise<{
    url: string | null;
    filename: string;
    contentType: string;
    expiresIn: number;
  }> {
    const qs = download ? '?download=true' : '';
    const response = await this.get<{
      url: string | null;
      filename: string;
      content_type: string; // eslint-disable-line @typescript-eslint/naming-convention
      expires_in: number; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/${recordId}/files/${fileId}/url${qs}`);
    return {
      url: response.url,
      filename: response.filename,
      contentType: response.content_type,
      expiresIn: response.expires_in,
    };
  }

  /**
   * Delete a file from a file_attachment field
   */
  async deleteFile(recordId: string, fileId: string): Promise<void> {
    await this.delete(`${this.basePath}/${recordId}/files/${fileId}`);
  }

  // ============================================================================
  // Record-Level Attachment Methods (independent of workflow fields)
  // ============================================================================

  /**
   * Request a pre-creation staging upload URL for a record attachment.
   * Used before the record exists — file is staged and moved on record creation.
   */
  async requestStagingUploadUrl(request: {
    filename: string;
    contentType: string;
    fileSize: number;
  }): Promise<{url: string | null; fileId: string; expiresIn: number}> {
    const response = await this.post<{
      url: string | null;
      file_id: string; // eslint-disable-line @typescript-eslint/naming-convention
      expires_in: number; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/attachments/upload-url`, {
      filename: request.filename,
      content_type: request.contentType, // eslint-disable-line @typescript-eslint/naming-convention
      file_size: request.fileSize, // eslint-disable-line @typescript-eslint/naming-convention
    });
    return {
      url: response.url,
      fileId: response.file_id,
      expiresIn: response.expires_in,
    };
  }

  /**
   * Request a post-creation upload URL for a record attachment.
   */
  async requestAttachmentUploadUrl(
    recordId: string,
    request: {filename: string; contentType: string; fileSize: number}
  ): Promise<{url: string | null; fileId: string; expiresIn: number}> {
    const response = await this.post<{
      url: string | null;
      file_id: string; // eslint-disable-line @typescript-eslint/naming-convention
      expires_in: number; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/${recordId}/attachments/upload-url`, {
      filename: request.filename,
      content_type: request.contentType, // eslint-disable-line @typescript-eslint/naming-convention
      file_size: request.fileSize, // eslint-disable-line @typescript-eslint/naming-convention
    });
    return {
      url: response.url,
      fileId: response.file_id,
      expiresIn: response.expires_in,
    };
  }

  /**
   * Confirm a staged attachment upload (post-creation).
   */
  async confirmAttachmentUpload(
    recordId: string,
    request: {
      fileId: string;
      filename: string;
      contentType: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<RecordAttachment> {
    const body: Record<string, unknown> = {
      file_id: request.fileId, // eslint-disable-line @typescript-eslint/naming-convention
      filename: request.filename,
      content_type: request.contentType, // eslint-disable-line @typescript-eslint/naming-convention
    };
    if (request.metadata) {
      body.metadata = request.metadata;
    }

    const response = await this.post<RecordAttachmentResponse>(
      `${this.basePath}/${recordId}/attachments/confirm-upload`,
      body
    );
    return this.convertAttachmentResponse(response);
  }

  /**
   * List all attachments for a record.
   */
  async listAttachments(recordId: string): Promise<RecordAttachment[]> {
    const response = await this.get<RecordAttachmentResponse[]>(
      `${this.basePath}/${recordId}/attachments`
    );
    return response.map((r) => this.convertAttachmentResponse(r));
  }

  /**
   * Get a presigned download URL for a record attachment.
   */
  async getAttachmentUrl(
    recordId: string,
    attachmentId: string,
    download = false
  ): Promise<{
    url: string;
    filename: string;
    contentType: string;
    expiresIn: number;
  }> {
    const qs = download ? '?download=true' : '';
    const response = await this.get<{
      url: string;
      filename: string;
      content_type: string; // eslint-disable-line @typescript-eslint/naming-convention
      expires_in: number; // eslint-disable-line @typescript-eslint/naming-convention
    }>(`${this.basePath}/${recordId}/attachments/${attachmentId}/url${qs}`);
    return {
      url: response.url,
      filename: response.filename,
      contentType: response.content_type,
      expiresIn: response.expires_in,
    };
  }

  /**
   * Delete a record attachment.
   */
  async deleteAttachment(
    recordId: string,
    attachmentId: string
  ): Promise<void> {
    await this.delete(
      `${this.basePath}/${recordId}/attachments/${attachmentId}`
    );
  }

  /**
   * Upload a record attachment via 3-phase presigned upload (post-creation).
   */
  async uploadAttachment(
    recordId: string,
    file: File,
    metadata?: Record<string, unknown>
  ): Promise<RecordAttachment> {
    const contentType = file.type || 'application/octet-stream';

    // Phase 1: Request presigned upload URL
    const urlResult = await this.requestAttachmentUploadUrl(recordId, {
      filename: file.name,
      contentType,
      fileSize: file.size,
    });

    if (!urlResult.url) {
      throw new Error('Upload URL not available. Check storage configuration.');
    }

    // Phase 2: PUT directly to presigned URL
    const headers = new Headers();
    headers.set('Content-Type', contentType);
    const uploadResponse = await fetch(urlResult.url, {
      method: 'PUT',
      body: file,
      headers,
    });

    if (!uploadResponse.ok) {
      throw new Error(
        `Upload failed with status ${String(uploadResponse.status)}`
      );
    }

    // Phase 3: Confirm upload
    return this.confirmAttachmentUpload(recordId, {
      fileId: urlResult.fileId,
      filename: file.name,
      contentType,
      metadata,
    });
  }

  /**
   * Export Record to PDF
   */
  async exportPdf(recordId: string): Promise<Blob> {
    return this.getBlob(`${this.basePath}/${recordId}/export-pdf`);
  }

  // ============================================================================
  // Private Conversion Methods
  // ============================================================================

  /**
   * Format display ID by wrapping the suffix portion in brackets.
   * Uses the record type prefix to reliably split prefix from suffix.
   * e.g., "NCR-0001" with prefix "NCR" → "NCR-[0001]"
   * e.g., "NCR-ALPHA" with prefix "NCR" → "NCR-[ALPHA]"
   */
  private formatDisplayId(rawId: string, prefix: string): string {
    if (!prefix) return rawId;

    // Try prefix + '-' + suffix first (e.g., "NCR-0001" with prefix "NCR")
    if (rawId.startsWith(prefix + '-')) {
      const suffix = rawId.slice(prefix.length + 1);
      return `${prefix} [${suffix}]`;
    }

    // Fallback: prefix directly followed by suffix (e.g., "F-1110002" with prefix "F-111")
    if (rawId.startsWith(prefix)) {
      const suffix = rawId.slice(prefix.length);
      if (suffix.length > 0) {
        return `${prefix} [${suffix}]`;
      }
    }

    return rawId;
  }

  /**
   * Convert Record list response from snake_case to camelCase
   */
  private convertRecordListResponse(
    response: RecordListResponse
  ): QmsRecordList {
    return {
      items: response.items.map((item) => this.convertRecordResponse(item)),
      total: response.total,
      limit: response.limit,
      offset: response.offset,
    };
  }

  /**
   * Convert Record response from snake_case to camelCase
   */
  private convertRecordResponse(response: RecordResponse): QmsRecord {
    // Archived takes precedence: deleted_at present → status is 'archived'
    const isArchived = Boolean(response.deleted_at);
    const status: QmsRecord['status'] = isArchived
      ? 'archived'
      : (response.status as QmsRecord['status']);

    return {
      id: response.id,
      tenantId: response.tenant_id,
      recordTypeId: response.record_type_id,
      recordTypeRevisionId: response.record_type_revision_id,
      displayId: this.formatDisplayId(
        response.display_id,
        response.record_type_prefix ?? ''
      ),
      rawDisplayId: response.display_id,
      name: response.name,
      status,
      currentStepOrder: response.current_step_order,
      totalSteps: response.total_steps,
      data: this.convertRecordData(response.data, response.fields),
      isArchived,
      deletedAt: response.deleted_at,
      archiveReason: response.archive_reason,
      createdBy: response.created_by,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
      recordTypeName: response.record_type_name,
      recordTypePrefix: response.record_type_prefix ?? '',
      fields: response.fields.map((f) => this.convertFieldResponse(f)),
      workflowSteps: response.workflow_steps.map((s) =>
        this.convertWorkflowStepResponse(s)
      ),
      stepInstances: response.step_instances.map((si) =>
        this.convertStepInstanceResponse(si)
      ),
      attachmentsEnabled: Boolean(response.attachments_enabled),
    };
  }

  /**
   * Convert record data, including e-signature and file attachment value conversion
   */
  private convertRecordData(
    data: Record<string, unknown>,
    fields?: FieldDefinitionResponse[]
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {...data};

    // Convert special field values (guard against undefined/empty fields)
    if (fields && Array.isArray(fields)) {
      for (const field of fields) {
        if (field.field_type === 'e_signature' && data[field.field_key]) {
          const sigData = data[field.field_key] as ESignatureValueResponse;
          result[field.field_key] = this.convertESignatureValue(sigData);
        }

        if (
          field.field_type === 'file_attachment' &&
          Array.isArray(data[field.field_key])
        ) {
          const files = data[field.field_key] as FileAttachmentValueResponse[];
          result[field.field_key] = files.map((f) =>
            this.convertFileAttachmentValue(f)
          );
        }
      }
    }

    return result;
  }

  /**
   * Convert e-signature value from snake_case to camelCase
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private convertESignatureValue(
    response: ESignatureValueResponse
  ): ESignatureValue {
    return {
      signedBy: response.signed_by,
      signedAt: response.signed_at,
    };
  }

  /**
   * Convert record attachment response from snake_case to camelCase
   */
  private convertAttachmentResponse(
    response: RecordAttachmentResponse
  ): RecordAttachment {
    return {
      id: response.id,
      recordId: response.record_id,
      filename: response.filename,
      contentType: response.content_type,
      fileSize: response.file_size,
      uploadedBy: response.uploaded_by,
      uploadedAt: response.uploaded_at,
      metadata: response.metadata ?? null,
    };
  }

  /**
   * Convert file attachment value from snake_case to camelCase
   */
  private convertFileAttachmentValue(
    response: FileAttachmentValueResponse
  ): FileAttachmentValue {
    return {
      id: response.id,
      filename: response.filename,
      contentType: response.content_type,
      fileSize: response.file_size,
      uploadedBy: response.uploaded_by,
      uploadedAt: response.uploaded_at,
      metadata: response.metadata ?? null,
    };
  }

  /**
   * Convert Field response from snake_case to camelCase
   */
  private convertFieldResponse(
    response: FieldDefinitionResponse
  ): FieldDefinition {
    return {
      name: response.field_key,
      label: response.label,
      fieldType: response.field_type as FieldDefinition['fieldType'],
      required: response.required,
      config: this.convertConfig(response.config),
      sortOrder: response.sort_order,
      description: response.description,
      conditions: response.conditions
        ? this.convertConditionRulesResponse(response.conditions)
        : undefined,
    };
  }

  /**
   * Convert config from snake_case to camelCase
   */
  private convertConfig(
    options?: Record<string, unknown>
  ): FieldDefinition['config'] {
    if (!options) return undefined;
    const result = convertFieldConfigResponse(options);
    return Object.keys(result).length > 0 ? result : undefined;
  }

  /**
   * Convert WorkflowStep response from snake_case to camelCase
   *
   * Note: Assignees are now configured at the record level, not template level.
   */
  private convertWorkflowStepResponse(
    response: WorkflowStepDefinitionResponse
  ): WorkflowStepDefinition {
    return {
      title: response.title,
      description: response.description,
      stepOrder: response.step_order,
      fieldKeys: response.field_keys,
      conditions: response.conditions
        ? this.convertConditionRulesResponse(response.conditions)
        : undefined,
    };
  }

  /**
   * Convert condition rules from backend format (snake_case) to frontend format (camelCase)
   */
  private convertConditionRulesResponse(
    rules: ConditionRuleResponse[]
  ): ConditionRule[] {
    return rules.map((rule) => ({
      triggerFieldKey: rule.trigger_field_key,
      operator: rule.operator as ConditionRule['operator'],
      value: rule.value,
      actionType: rule.action_type as ConditionRule['actionType'],
    }));
  }

  /**
   * Convert StepInstance response from snake_case to camelCase
   */
  private convertStepInstanceResponse(
    response: StepInstanceResponse
  ): StepInstance {
    return {
      id: response.id,
      stepOrder: response.step_order,
      title: response.title,
      description: response.description,
      assignedUserIds: response.assigned_user_ids ?? [],
      assignedGroupIds: response.assigned_group_ids ?? [],
      status: response.status as StepInstance['status'],
      fieldKeys: response.field_keys,
      completedAt: response.completed_at,
      completedBy: response.completed_by,
    };
  }
}

/**
 * Singleton factory for RecordsApiClient
 *
 * The client is now self-sufficient (fetches auth headers dynamically),
 * so we use a single cached instance for production.
 * Passing a token creates a fresh instance for testing.
 */
let cachedClient: RecordsApiClient | undefined;

export function getRecordsApi(token?: string): RecordsApiClient {
  // Testing: fresh instance with injected token
  if (token) {
    return new RecordsApiClient(token);
  }

  // Production: singleton instance (self-sufficient auth)
  cachedClient ??= new RecordsApiClient();

  return cachedClient;
}
